import React from 'react'
import type { Run } from '../api/client'

/**
 * Per-leg SLA cell for the run tables.
 *
 * REPLACES a duplicated `slaCell` in RunHistory and GlobalRunHistory that had a real
 * defect in both copies:
 *
 *     if (out === false || inb === false) return '✓ Pass'
 *
 * That fired whenever EITHER leg had passed, so a run whose outbound leg passed and
 * whose inbound leg was unknown, unmeasured, or not under an SLA displayed an
 * unqualified green "✓ Pass". One success was being presented as the whole verdict.
 *
 * The rules here instead:
 *
 *   - Any leg breached  -> the cell reads BREACH and names which leg(s). A breach is
 *     never outweighed by the other leg passing. Per-leg thresholds are authoritative:
 *     a run passes only when EVERY CONFIGURED leg passes, so a leg breach fails the run
 *     even when the total elapsed time fits inside the sum of the legs.
 *   - All evaluable legs passed -> Pass.
 *   - Some legs passed, others have no verdict -> "Partial", NOT Pass. The run has
 *     not demonstrated compliance yet and saying otherwise is the original bug.
 *   - A leg with no threshold -> "not evaluated". Not a pass, and nothing stands behind
 *     it: the job-wide fallback that used to is retired.
 *   - No verdicts at all -> em dash.
 *
 * Each leg also shows its measured DURATION, because "which leg" and "how long" are
 * the two things you actually want from this column, and the timing was previously
 * only visible by opening the run.
 */

const RED = '#f44336'
const GREEN = '#4caf50'
const GREY = '#9aa0a6'
const AMBER = '#ffa726'

function mins(seconds: number | null | undefined): string {
  if (typeof seconds !== 'number') return ''
  return `${(seconds / 60).toFixed(1)}m`
}

/** One leg's verdict + duration. */
function Leg({
  label, breached, seconds, thresholdMinutes,
}: {
  label: string
  breached: boolean | null | undefined
  seconds: number | null | undefined
  thresholdMinutes: number | null | undefined
}) {
  const d = mins(seconds)

  // No threshold on this leg, so the leg is NOT EVALUATED. Checked FIRST: this and
  // "pending" both arrive as a null verdict but they are different facts, and only one
  // of them will ever change. `=== null` specifically — undefined means the API did not
  // send the field (older payload), which must not read as "not evaluated".
  //
  // Labelled "not evaluated" rather than "N/A" or "no SLA" deliberately: there is no
  // job-wide fallback behind a blank leg any more, and a blank leg is not a pass.
  if (thresholdMinutes === null) {
    return (
      <span
        style={{ color: GREY }}
        title={`${label}: not evaluated — no threshold on this leg. Not a pass; no verdict will arrive.`}
      >
        {label} not evaluated{d && ` ${d}`}
      </span>
    )
  }
  if (breached === null || breached === undefined) {
    return (
      <span style={{ color: GREY }} title={`${label}: not evaluated yet`}>
        {label} –{d && ` ${d}`}
      </span>
    )
  }
  return (
    <span
      style={{ color: breached ? RED : GREEN, fontWeight: breached ? 600 : 500 }}
      title={`${label}: ${breached ? 'breached' : 'within'} ${thresholdMinutes}m threshold`}
    >
      {label} {breached ? '⚠️' : '✓'}{d && ` ${d}`}
    </span>
  )
}

export function SlaCell({ run }: { run: Run }) {
  const out = run.outbound_sla_breached
  const inb = run.inbound_sla_breached

  // A BATCH row is an aggregate over its items, not a single measurement. Its verdicts come
  // from the batch's stored statistics — "did at least X% of evaluable items meet this leg's
  // SLA" — and it has no per-leg DURATION of its own, so rendering the usual leg timings would
  // imply a measurement that does not exist. Shown as two aggregate verdicts instead, with the
  // denominator available in the batch report.
  if (run.kind === 'batch') {
    if (out === null && inb === null) {
      return (
        <span
          style={{ fontSize: '0.78rem', color: GREY, cursor: 'help' }}
          title={
            'No verdict yet. A batch is judged only once its statistics are computed, and a leg ' +
            'with no evaluable items has no pass rate to report. Open the report for the ' +
            'denominators.'
          }
        >
          – aggregate
        </span>
      )
    }
    const legs = [
      { label: 'out', v: out },
      { label: 'in', v: inb },
    ]
    const anyBreach = legs.some(l => l.v === true)
    return (
      <span
        style={{ fontSize: '0.78rem', display: 'inline-flex', gap: '0.45rem', cursor: 'help' }}
        title={
          'Aggregate over the batch: each leg passes when at least the required percentage of ' +
          'EVALUABLE items met that leg. Open the report for met/evaluable and the exclusions.'
        }
      >
        {legs.map(l => (
          <span
            key={l.label}
            style={{
              color: l.v === true ? RED : l.v === false ? GREEN : GREY,
              fontWeight: l.v === true ? 600 : 500,
            }}
          >
            {l.label} {l.v === true ? '⚠️' : l.v === false ? '✓' : '–'}
          </span>
        ))}
        <span style={{ color: anyBreach ? RED : GREY, fontSize: '0.7rem' }}>agg</span>
      </span>
    )
  }

  // NO `sla_basis === 'combined'` BRANCH, AND THERE MUST NOT BE ONE AGAIN.
  //
  // `combined` and `combined_fallback` are RETIRED basis values that no backend path emits
  // (backend/schemas/run.py states this at the field). They named a combined THRESHOLD that
  // drove BOTH leg verdicts, forgiving a breached leg whenever the other made up the time.
  // What exists now is a different thing under a similar name: a DERIVED budget in its own
  // column, `combined_sla_breached`, which is an ADDITIONAL constraint and can only tighten a
  // verdict. It is never a basis, so it never reaches this cell as one.
  //
  // The branch that used to be here rendered a verdict for a basis nothing produces. It was
  // dead residue of the retired implementation, which is precisely the thing the mutation
  // guard in tests/test_sla_per_leg_authoritative.py exists to keep from coming back — so
  // deleting it is part of not letting it return, not a separate tidy-up. A row whose
  // `t_fax_sent` never arrived now falls through to the per-leg render below, where each
  // unmeasured leg reads "not evaluated" on its own account rather than through a basis.

  const legs = (
    <span style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem', fontSize: '0.72rem' }}>
      <Leg label="Out" breached={out} seconds={run.outbound_sla_seconds}
           thresholdMinutes={run.outbound_sla_minutes_effective} />
      <Leg label="In" breached={inb} seconds={run.inbound_sla_seconds}
           thresholdMinutes={run.inbound_sla_minutes_effective} />
    </span>
  )

  // Headline verdict. A breach on EITHER leg makes the whole cell a breach — it is
  // never cancelled out by the other leg passing, which is the behaviour that was
  // reported as wrong.
  const anyBreach = out === true || inb === true
  const passes = [out, inb].filter(v => v === false).length
  const verdicts = [out, inb].filter(v => v === true || v === false).length
  const evaluable = [
    run.outbound_sla_minutes_effective,
    run.inbound_sla_minutes_effective,
  ].filter(t => t !== null).length

  let headline: React.ReactNode
  if (anyBreach) {
    headline = <span style={{ color: RED, fontWeight: 700 }}>⚠️ Breach</span>
  } else if (verdicts === 0) {
    headline = <span style={{ color: GREY }}>—</span>
  } else if (passes >= evaluable && evaluable > 0) {
    headline = <span style={{ color: GREEN, fontWeight: 600 }}>✓ Pass</span>
  } else {
    // Some leg passed but another has no verdict yet. NOT a pass: the run has not
    // demonstrated end-to-end compliance, and calling it one is the original defect.
    headline = (
      <span style={{ color: AMBER, fontWeight: 600 }} title="One leg passed; the other has no verdict yet">
        Partial
      </span>
    )
  }

  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', fontSize: '0.78rem' }}>
      {headline}
      {legs}
    </span>
  )
}
